import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface LanguageContextType {
  currentLang: string;
  setLanguage: (lang: string) => Promise<void>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const [currentLang, setCurrentLang] = useState(i18n.language);

  useEffect(() => {
    // Update HTML lang attribute when language changes
    document.documentElement.lang = currentLang;
  }, [currentLang]);

  // T039 [US2]: Implement language change handler with scroll position preservation
  // T051 [US3]: Implement localStorage save on language change
  const setLanguage = useCallback(async (lang: string) => {
    // Save current scroll position
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    try {
      await i18n.changeLanguage(lang);
      setCurrentLang(lang);

      // Explicitly save to localStorage (FR-011)
      try {
        localStorage.setItem('happytech_language', lang);
      } catch (error) {
        // Handle quota exceeded or disabled storage gracefully (FR-013)
        console.warn('[i18n] Could not save language preference to localStorage:', error);
        // Application continues to work, just doesn't persist
      }

      console.log(`[i18n] Language changed to: ${lang}`);

      // Restore scroll position after language change (FR-008)
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        window.scrollTo(scrollX, scrollY);
      });
    } catch (error) {
      console.error(`[i18n] Failed to change language: ${error}`);
      throw error;
    }
  }, [i18n]);

  return (
    <LanguageContext.Provider value={{ currentLang, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}
